import React, { useState, useEffect } from 'react';
import { X, Check } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';

/**
 * Modal for batch assigning responsible persons to selected entities.
 * Allows picking a position + employee pair, then applies to all selected items.
 */
const BatchAssignResponsibleModal = ({ entityType, selectedIds, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [positions, setPositions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [positionId, setPositionId] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(0);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    // Load employees (from assignable-employees endpoint)
    fetch(`/api/${entityType}s/${selectedIds[0]}/assignable-employees`)
      .then(r => r.json())
      .then(d => {
        if (d.status === 'success') setEmployees(d.data || []);
      })
      .catch(() => {});

    // Load positions (from positions endpoint, not employee-positions)
    fetch('/api/organization/positions')
      .then(r => r.json())
      .then(d => {
        if (d.status === 'success') setPositions(d.data || []);
      })
      .catch(() => {});
  }, [entityType, selectedIds]);

  const handleSubmit = async () => {
    if (!positionId || !employeeId) return;
    setSubmitting(true);
    setErrors([]);
    setDone(0);

    const total = selectedIds.length;
    const errs = [];

    for (let i = 0; i < total; i++) {
      const id = selectedIds[i];
      try {
        const res = await fetch(`/api/${entityType}s/${id}/responsible-persons/add`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            position_id: parseInt(positionId, 10),
            employee_id: parseInt(employeeId, 10),
          }),
        });
        const data = await res.json();
        if (data.status !== 'success') {
          errs.push(`ID ${id}: ${data.message || t('common.request_failed')}`);
        }
      } catch (e) {
        errs.push(`ID ${id}: ${e.message}`);
      }
      setDone(i + 1);
    }

    setErrors(errs);
    setSubmitting(false);

    if (errs.length === 0) {
      onSuccess();
    }
  };

  const selectedPos = positions.find(p => p.id === parseInt(positionId, 10));
  const selectedEmp = employees.find(e => e.entity_id === parseInt(employeeId, 10));

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[99999]" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            {t('common.batch_assign_responsible')}
          </h2>
          <button onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          <div className="text-sm text-slate-500 dark:text-slate-400">
            <span dangerouslySetInnerHTML={{ __html: t('common.selected_records_count', { count: selectedIds.length }) }} />
          </div>

          {/* Position selector */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              {t('common.responsible_position')}
            </label>
            <select
              value={positionId}
              onChange={e => setPositionId(e.target.value)}
              className="w-full p-2.5 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
            >
              <option value="">{t('common.select_position_placeholder')}</option>
              {positions.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* Employee selector */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              {t('common.responsible_employee')}
            </label>
            <select
              value={employeeId}
              onChange={e => setEmployeeId(e.target.value)}
              className="w-full p-2.5 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
            >
              <option value="">{t('common.select_employee_placeholder')}</option>
              {employees.map(e => (
                <option key={e.entity_id} value={e.entity_id}>{e.entity_id} - {(e.first_name || '')}{(e.last_name || '')}</option>
              ))}
            </select>
          </div>

          {/* Preview */}
          {positionId && employeeId && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm">
              <span dangerouslySetInnerHTML={{ __html: t('common.assign_preview', { count: selectedIds.length }) }} />
              <br />
              <span className="font-medium">{selectedPos?.name || '—'}</span> → <span className="text-blue-600 dark:text-blue-400">{selectedEmp ? `${selectedEmp.entity_id} - ${(selectedEmp.first_name || '')}${(selectedEmp.last_name || '')}` : '—'}</span>
            </div>
          )}

          {/* Progress */}
          {submitting && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-slate-500">
                <span>{t('common.assign_in_progress')}</span>
                <span>{done} / {selectedIds.length}</span>
              </div>
              <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                <div className="bg-violet-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(done / selectedIds.length) * 100}%` }} />
              </div>
            </div>
          )}

          {/* Errors */}
          {errors.length > 0 && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 max-h-32 overflow-y-auto">
              <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-1">{t('common.assign_partial_failure')}</p>
              {errors.map((err, i) => (
                <p key={i} className="text-xs text-red-500 dark:text-red-300">{err}</p>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors">
            {t('common.cancel')}
          </button>
          <button onClick={handleSubmit}
            disabled={!positionId || !employeeId || submitting}
            className="flex items-center gap-2 bg-violet-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-50 transition-all">
            <Check size={16} />
            {submitting ? t('common.assign_in_progress') : t('common.assign_with_count', { count: selectedIds.length })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BatchAssignResponsibleModal;
